import clsx from 'clsx';
import { useState } from 'react';
import { FiAlertTriangle, FiExternalLink, FiInfo } from 'react-icons/fi';
import { TbSailboat } from 'react-icons/tb';

import {
  MANUALLY_GRANTED_MESSAGE,
  isManuallyGrantedPlan,
  isPaymentMethodMissing,
} from '@/common/error';
import { Modal } from '@/components';
import {
  FormButton,
  FormButtonOutlined,
  FormGroup,
  NumberStepper,
  Radio,
} from '@/components/FormInputs/FormInputs';
import {
  useSubscriptionCheckoutMutation,
  useUpdateSubscriptionMutation,
} from '@/store/subscriptions/subscriptions.store';
import {
  SubscriptionTier,
  createCheckoutSubscriptionSchema,
  type CreateCheckoutSubscription,
  type PaidSubscriptionTier,
  type Subscription,
} from '@/store/subscriptions/subscriptions.types';
import type { Vessel } from '@/store/vessels/vessels.types';

import {
  DEFAULT_INTERVAL,
  INTERVAL_CHOICES,
  INTERVAL_UNIT,
  MIN_CREW_SEATS,
  MIN_SEATS,
  TIER_CHOICES,
  billedEvery,
  isPaidPlan,
} from '../subscription.display';
import { fmtDateTime } from '../subscription.helpers';
import s from '../subscription.module.scss';
import { remainderOnTakeover, type PlanRemainder } from '../subscription.takeover';
import { useBillingPortal } from '../useBillingPortal';
import { usePlanQuote } from '../usePlanQuote';

/**
 * Whether this vessel's payer has to answer for the plan's reserved management seat.
 *
 * Only a vessel with no owner does: a captain paying for one is buying a seat that has no
 * owner to belong to yet, so they say whether it is theirs or is being kept for the owner
 * they are going to add. A vessel that has an owner never asks — the seat is the owner's
 * whatever anyone answers, and the server ignores the field for it.
 */
const asksAboutOwner = (vessel: Vessel): boolean => !vessel.ownerId;

/**
 * The plan the form starts from: an existing paid row is edited from where it stands, and
 * a vessel with nothing to edit starts on the cheapest paid plan.
 *
 * The owner answer is loaded the same way — from the row, so the question opens on the
 * answer already stored rather than putting it again from scratch — and is left off
 * entirely for a vessel that is not asked, which keeps the field out of the request too.
 */
const initialInput = (
  vessel: Vessel,
  subscription: Subscription | null,
): CreateCheckoutSubscription => {
  const willAddOwner = asksAboutOwner(vessel)
    ? { willAddOwner: subscription?.captainWillAddOwner ?? false }
    : {};

  if (!isPaidPlan(subscription) || !subscription.tier) {
    return {
      tier: SubscriptionTier.NAVIGATE,
      interval: DEFAULT_INTERVAL,
      seats: MIN_SEATS,
      crewSeats: MIN_CREW_SEATS,
      ...willAddOwner,
    };
  }

  return {
    tier: subscription.tier as PaidSubscriptionTier,
    interval: subscription.interval ?? DEFAULT_INTERVAL,
    seats: subscription.seats,
    crewSeats: subscription.crewSeats,
    ...willAddOwner,
  };
};

/**
 * The reserved management seat, as a question. Both answers cost the same — this moves one
 * seat between two people, it does not add one — so what it needs to say is who ends up in it.
 */
function OwnerSeatField({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (willAddOwner: boolean) => void;
}) {
  return (
    <>
      <FormGroup label="Are you adding an owner to this vessel?" cols={2}>
        <Radio label="Yes, one is coming" checked={value} onChange={() => onChange(true)} />
        <Radio label="No — I run her" checked={!value} onChange={() => onChange(false)} />
      </FormGroup>
      <p className={s.formHint}>
        {value
          ? 'One management seat is kept empty for them and cannot be given to anybody else. It becomes theirs the moment they come aboard.'
          : 'The plan’s reserved management seat is yours. You can change this at any time, and it stops mattering once an owner joins.'}
      </p>
    </>
  );
}

/** What a remainder is called when the notice names it. */
const REMAINDER_NAME: Record<PlanRemainder['source'], string> = {
  paid: 'paid time',
  trial: 'free trial',
};

/**
 * What buying a plan somebody else is already paying for actually does, said before the form
 * rather than after the charge. All of it is things the server decides and nothing on this
 * screen would otherwise show: the current plan keeps running until this payment clears
 * (`stopSupersededPlan` only runs off the completed checkout), and whatever it had left —
 * days already paid for, or the rest of the vessel's trial — arrives as free time on the new
 * one (`purchase.carriedOver`).
 *
 * Except when there is too little of it left. Under 48 hours, the remainder cannot be carried
 * at all and is simply dropped — see `remainderOnTakeover`. That turns the notice from an
 * assurance into a warning, and it is the only thing on this screen that would tell the owner
 * to wait a day and buy the plan for nothing rather than now and lose the rest, so it is said
 * plainly, with the deadline it turns on.
 */
function TakeoverNotice({ subscription }: { subscription: Subscription | null }) {
  const remainder = remainderOnTakeover(subscription);
  const isLosing = !!remainder && !remainder.isCarried;

  return (
    <div className={isLosing ? s.takeoverNoticeWarn : s.takeoverNotice}>
      {isLosing ? (
        <FiAlertTriangle className={s.takeoverNoticeWarnIcon} />
      ) : (
        <FiInfo className={s.takeoverNoticeIcon} />
      )}
      <p className={isLosing ? s.takeoverNoticeWarnText : s.takeoverNoticeText}>
        {remainder === null && (
          // Past due, unpaid or paused: a period Stripe never collected was never bought, so
          // there is nothing to promise and nothing to warn about either.
          <>
            This vessel’s plan is paid by somebody else. Nothing about theirs changes until your
            payment clears — and from then the plan is yours, billed to you.
          </>
        )}
        {remainder?.isCarried && (
          <>
            This vessel’s plan is paid by somebody else. Nothing about theirs changes until your
            payment clears — and when it does, the {REMAINDER_NAME[remainder.source]} this plan
            still has to run — through {fmtDateTime(remainder.until)} — carries over to yours before
            you are billed anything.
          </>
        )}
        {isLosing && (
          <>
            This vessel’s plan is paid by somebody else, and its {REMAINDER_NAME[remainder.source]}{' '}
            runs out {fmtDateTime(remainder.until)} — <strong>less than two days away</strong>. That
            is too short a stretch to move onto a new plan, so{' '}
            <strong>the time left will be lost</strong>: taking the plan over now charges you in
            full straight away, with none of it carried across. Nothing about theirs changes until
            your payment clears.
          </>
        )}
      </p>
    </div>
  );
}

/**
 * Shown when the change was refused for want of a card on file. Retrying cannot help, so
 * this replaces the generic error with the one thing that does: a trip to Stripe's billing
 * portal, from where the browser comes back to this page and the change can be made again.
 */
function PaymentMethodNotice({ serviceId }: { serviceId: string }) {
  const portal = useBillingPortal(serviceId);

  return (
    <div className={s.formNotice}>
      <p className={s.formError}>
        This vessel has no payment method on file, so the plan could not be changed — nothing has
        been charged. Open <strong>Manage payment information</strong> below, add a card in Stripe,
        then come back and change the plan again.
      </p>
      {/* The page header's button, repeated down to its shape and wording: the sentence
          above points at it by name, and the header's own copy is behind the modal overlay
          for as long as this is on screen. */}
      <button
        type="button"
        className={s.portalBtn}
        onClick={portal.open}
        disabled={!portal.canOpen}
      >
        <FiExternalLink />
        <span>{portal.isRedirecting ? 'Preparing…' : 'Manage payment information'}</span>
      </button>
      {portal.isError && (
        <p className={s.formError}>Could not open the billing portal. Please try again.</p>
      )}
    </div>
  );
}

const isSamePlan = (input: CreateCheckoutSubscription, subscription: Subscription) =>
  input.tier === subscription.tier &&
  input.interval === subscription.interval &&
  input.seats === subscription.seats &&
  (input.crewSeats ?? MIN_CREW_SEATS) === subscription.crewSeats &&
  // Answering the owner question is a change of its own: it moves a seat, even when every
  // priced field is untouched.
  (input.willAddOwner ?? subscription.captainWillAddOwner) === subscription.captainWillAddOwner;

/**
 * Sets one vessel's plan — tier, interval, seats and the crew-seat add-on. The vessel is
 * fixed by the row the user clicked, so its name is shown rather than collected.
 *
 * Which endpoint that goes to depends on whether there is a live Stripe subscription behind
 * the vessel — i.e. a paid plan:
 *
 *  - a paid plan → `PATCH /subscriptions/{id}`, which changes tier, interval or seat counts
 *    in place (204);
 *  - harbor, no row at all, or an abandoned checkout → `POST /subscriptions/checkout`, which
 *    redirects to Stripe. Harbor has no Stripe object to edit, so leaving it is a purchase
 *    rather than an edit; the server admits a live harbor row here for exactly that reason.
 *
 * `isTakeover` overrides the first case: the vessel does hold a paid plan, but it is not the
 * caller's to edit, so this is a purchase too. The body is the same one a first purchase
 * sends — the server reads the takeover off the row and the caller, not off any field — and
 * the owner question is not asked, since a vessel with an owner never asks it.
 */
function VesselPlanForm({
  vessel,
  subscription,
  start,
  isTakeover,
  onClose,
}: {
  vessel: Vessel;
  subscription: Subscription | null;
  start?: Partial<CreateCheckoutSubscription>;
  isTakeover: boolean;
  onClose: () => void;
}) {
  const { id: serviceId, name: vesselName } = vessel;
  const [input, setInput] = useState<CreateCheckoutSubscription>(() => ({
    ...initialInput(vessel, subscription),
    ...start,
  }));

  const checkout = useSubscriptionCheckoutMutation();
  const update = useUpdateSubscriptionMutation();

  // What the draft costs, priced as it stands — the unit prices beside each field and the
  // total below them all come from here.
  const quote = usePlanQuote(serviceId, input);
  const unit = INTERVAL_UNIT[input.interval];
  const seatPrice = quote.seat(input.tier);

  const parsed = createCheckoutSubscriptionSchema.safeParse(input);

  // Only a paid plan the caller pays for is edited in place; every other path is a checkout
  // that ends at Stripe.
  const isEditing = !isTakeover && isPaidPlan(subscription);
  const isPending = checkout.isPending || update.isPending;
  // A takeover is never "unchanged": buying the plan exactly as it stands is the whole point.
  const isUnchanged = isEditing && isSamePlan(input, subscription);

  // Stripe refuses the change outright when the customer has no card on file. That is a
  // missing prerequisite rather than a failed attempt, so it gets its own notice below.
  const needsPaymentMethod = isPaymentMethodMissing(update.error ?? checkout.error);

  const selectTier = (tier: PaidSubscriptionTier) => setInput((d) => ({ ...d, tier }));

  const submit = async () => {
    if (!parsed.success) return;

    if (isEditing) {
      // Every field is sent rather than only the changed ones — the draft is the plan as a
      // whole, and the server takes tier, interval and both seat counts together. The owner
      // answer especially: PATCH overwrites it with whatever arrives, so sending the draft
      // whole is what keeps a seat change from quietly un-reserving the owner's seat.
      const { url } = await update.mutateAsync({
        serviceId,
        id: subscription.id,
        input: parsed.data,
      });
      // A paid row is edited in place, so the answer is 204 and the queries are already
      // invalidated. The endpoint's other answer — a checkout URL — belongs to the harbor
      // row it is no longer given, and is honoured rather than dropped on the floor.
      if (!url) return onClose();
      return window.location.assign(url);
    }

    // The subscription row is created as `incomplete` here — it only becomes active once
    // Stripe reports the completed checkout over the webhook.
    const url = await checkout.mutateAsync({ serviceId, input: parsed.data });
    window.location.assign(url);
  };

  const submitLabel = () => {
    if (isPending) return isEditing ? 'Saving…' : 'Redirecting…';
    return isEditing ? 'Save changes' : 'Continue to payment';
  };

  return (
    <div className={s.modalBody}>
      {isTakeover && <TakeoverNotice subscription={subscription} />}

      {/* Vessel and plan on one line, as the calculator rows them up. The vessel takes the
          slack so the switcher beside it is never squeezed. */}
      <div className={s.planRow}>
        {/* The vessel cell from the wizard's plan calculator — the same field shape, but the
            row the modal was opened from already picked the vessel, so it only states it. */}
        <FormGroup label="Vessel" cols={1}>
          <div className={s.vesselField}>
            <TbSailboat className={s.vesselFieldIcon} />
            {/* Clipped at the field's width, so the full name is kept on the title. */}
            <span className={s.vesselFieldName} title={vesselName}>
              {vesselName}
            </span>
          </div>
        </FormGroup>

        {/* Two mutually exclusive tiers read better as one switcher than as two radios —
            the same control the onboarding wizard's plan calculator uses. */}
        <FormGroup label="Plan" cols={1}>
          <div className={s.tierField}>
            <div className={s.tierToggle} role="radiogroup" aria-label="Plan">
              {TIER_CHOICES.map(({ tier, label }) => (
                <button
                  key={tier}
                  type="button"
                  role="radio"
                  aria-checked={input.tier === tier}
                  className={clsx(s.tierOpt, input.tier === tier && s.tierOptActive)}
                  onClick={() => selectTier(tier)}
                >
                  {label}
                </button>
              ))}
            </div>
            {/* The selected tier's price only — an unpriced tier carries no caption at all
                rather than a placeholder. */}
            {seatPrice && <span className={s.unitPrice}>{`${seatPrice}${unit} · per seat`}</span>}
          </div>
        </FormGroup>
      </div>

      {/* Only a vessel still waiting for an owner asks; every other one has the seat settled
          already. Above the seat counts, because it is about who fills one of them. */}
      {asksAboutOwner(vessel) && (
        <OwnerSeatField
          value={input.willAddOwner ?? false}
          onChange={(willAddOwner) => setInput((d) => ({ ...d, willAddOwner }))}
        />
      )}

      {/* With a single interval there is nothing to choose, so the field is omitted
          entirely; the value still goes to the API as DEFAULT_INTERVAL. */}
      {INTERVAL_CHOICES.length > 1 && (
        <FormGroup label="Billing interval" cols={2}>
          {INTERVAL_CHOICES.map(({ interval, label }) => (
            <Radio
              key={interval}
              label={label}
              checked={input.interval === interval}
              onChange={() => setInput((d) => ({ ...d, interval }))}
            />
          ))}
        </FormGroup>
      )}

      {/* Both counts are free on either tier — the seat model is the same, only the price
          per seat differs. */}
      <div className={s.formGrid}>
        <div className={s.pricedField}>
          <NumberStepper
            label="Management"
            min={MIN_SEATS}
            value={input.seats}
            onChange={(seats) => setInput((d) => ({ ...d, seats }))}
          />
          {seatPrice && <span className={s.unitPrice}>{`${seatPrice}${unit} each`}</span>}
        </div>
        <div className={s.pricedField}>
          <NumberStepper
            label="Crew"
            min={MIN_CREW_SEATS}
            value={input.crewSeats ?? MIN_CREW_SEATS}
            onChange={(crewSeats) => setInput((d) => ({ ...d, crewSeats }))}
          />
          {quote.crew && <span className={s.unitPrice}>{`${quote.crew}${unit} each`}</span>}
        </div>
      </div>

      {/* Always on screen, whatever the two counts are. A price the sum needs but did not
          get back leaves an em dash, the same way the vessel's own bill reads — a partial
          sum would understate what is about to be charged. */}
      <div className={s.totalBar}>
        <span className={s.totalNote}>{billedEvery(input.interval)}</span>
        <span className={s.totalAmount}>
          <span className={s.totalLabel}>Total</span>
          <strong>{quote.total ?? '—'}</strong>
          {quote.total && <span className={s.totalUnit}>{unit}</span>}
        </span>
      </div>

      {needsPaymentMethod ? (
        <PaymentMethodNotice serviceId={serviceId} />
      ) : (
        (checkout.isError || update.isError) && (
          <p className={s.formError}>
            {isManuallyGrantedPlan(update.error ?? checkout.error)
              ? MANUALLY_GRANTED_MESSAGE
              : 'Could not change the plan. Please try again.'}
          </p>
        )
      )}

      <div className={s.modalActions}>
        <FormButtonOutlined onClick={onClose} disabled={isPending}>
          Cancel
        </FormButtonOutlined>
        <FormButton disabled={!parsed.success || isPending || isUnchanged} onClick={submit}>
          {submitLabel()}
        </FormButton>
      </div>
    </div>
  );
}

const modalTitle = (subscription: Subscription | null, isTakeover: boolean) => {
  if (isTakeover) return 'Take over the plan';
  return isPaidPlan(subscription) ? 'Change plan' : 'Start a subscription';
};

export function VesselPlanModal({
  open,
  vessel,
  subscription,
  start,
  isTakeover = false,
  onClose,
}: {
  open: boolean;
  /** The whole vessel, not just its name: whether it has an owner decides what is asked. */
  vessel: Vessel;
  /** The vessel's current subscription, if any — this is what picks create versus update. */
  subscription: Subscription | null;
  /** Fields the form opens on in place of the current plan's — e.g. an upgrade to Command. */
  start?: Partial<CreateCheckoutSubscription>;
  /**
   * The plan is real but somebody else pays for it, and the caller owns the vessel — so this
   * is a purchase, not an edit. Decided by `canTakeOverPlan` rather than re-derived here;
   * this only says which of the two the form is.
   */
  isTakeover?: boolean;
  onClose: () => void;
}) {
  return (
    <Modal open={open} onClose={onClose} title={modalTitle(subscription, isTakeover)}>
      {/* Mounted only while open, so each run starts from the plan as it stands now. */}
      <VesselPlanForm
        vessel={vessel}
        subscription={subscription}
        start={start}
        isTakeover={isTakeover}
        onClose={onClose}
      />
    </Modal>
  );
}
